import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import { t, tnc } from '@owl-nest/localize'
import { useCopy } from '@owl-nest/copier'

import * as plume from '@ulule/owl-kit-components/next'

import { RootState } from '../../reducers'
import * as projectModel from '../models/project'
import * as modalActions from '../../actions/modal'
import { useNavigation } from '../hooks/useNavigation'
import { PATHS } from '../paths'
import { Link as RouterLink } from 'react-router-dom'
import { ComingSoonContent } from '../views/coming-soon'
import { BrowserScreenshot } from './BrowserScreenshot'

export function ShareOverlay(): React.ReactElement<unknown> {
  const { canCopy, copy, copyStatus } = useCopy()
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const isShareOverlayOpen = Redux.useSelector<RootState, boolean>((state) => state.application.shareOverlay.open)
  const dispatch = Redux.useDispatch()
  const navigation = useNavigation()

  const canonicalComingSoon = `${window.location.origin}/${project.slug}/coming-soon`

  if (isShareOverlayOpen) {
    window.location.hash = 'share'
  }

  return (
    <ShareOverlayModal
      closable
      open={isShareOverlayOpen}
      onClose={() => {
        // remove hash from url when modale is closed
        // we used replaceState because we need to remove completly hash
        // if we have a empty hash, it's make a scroll into project information
        history.replaceState(null, '', ' ')
        dispatch(modalActions.toggleShareOverlay(false))
      }}
    >
      <Texts>
        <plume.illustrations.twoToned.Share size={90} />
        <plume.styles.heading.L>{t('Share your Ulule coming soon page')}</plume.styles.heading.L>
        <plume.styles.copy.S.regular>
          {t(
            'Prepare for your launch by sharing your "Coming Soon" page with your first circle and community. Each fan will automatically receive an email when your project is launched!',
          )}
        </plume.styles.copy.S.regular>
      </Texts>

      <Screenshot href={canonicalComingSoon}>
        <ComingSoonContent iframe />
      </Screenshot>

      <Texts>
        {!navigation.is(PATHS.COMING_SOON) && (
          <plume.styles.button.Button
            as={RouterLink}
            size="medium"
            onClick={openComingSoon}
            to={navigation.pathTo(PATHS.COMING_SOON)}
            target="_blank"
          >
            {t('Open coming soon page')}
          </plume.styles.button.Button>
        )}

        <CopyInput>
          <plume.styles.input.Input type="text" disabled value={canonicalComingSoon} />
          {canCopy && copyStatus && (
            <plume.styles.tooltip.BlockAnchor>
              <plume.Tooltip fitToContent position="top" open={copyStatus !== 'idle'}>
                {copyStatus === 'succeeded' && (
                  <>
                    <plume.glyphs.stroke.CheckCircle size={16} />
                    <plume.styles.copy.S.regular>{t('Link copied to your clipboard.')}</plume.styles.copy.S.regular>
                  </>
                )}
                {copyStatus === 'failed' && (
                  <>
                    <plume.glyphs.stroke.TimesCircle size={16} />
                    <plume.styles.copy.S.regular>
                      {t("Sorry, we couldn't copy the link. Please try again!")}
                    </plume.styles.copy.S.regular>
                  </>
                )}
              </plume.Tooltip>
              <plume.LinkAsButton onClick={() => copy(canonicalComingSoon)} tinted>
                <plume.glyphs.stroke.Link size={16} />
                {t('Copy link')}
              </plume.LinkAsButton>
            </plume.styles.tooltip.BlockAnchor>
          )}
        </CopyInput>
        <plume.styles.copy.S.regular>
          {project.fans_count === 0
            ? t("You don't have any fan yet, share your link!")
            : tnc('You currently have [count] fan', 'You currently have [count] fans', project.fans_count, {
                count: (
                  <plume.styles.heading.S as="span" dangerouslySetInnerHTML={{ __html: String(project.fans_count) }} />
                ),
              })}
        </plume.styles.copy.S.regular>
      </Texts>
    </ShareOverlayModal>
  )

  function openComingSoon(): void {
    dispatch(modalActions.toggleShareOverlay(false))
  }
}

const CopyInput = styled.div`
  display: flex;

  ${plume.styles.input.Input} {
    flex: 1;
  }

  ${plume.LinkAsButton} {
    flex: 0 0 auto;
  }

  ${plume.styles.tooltip.BlockAnchor} {
    display: inline-block;
    margin-left: 12px;
    margin-bottom: 12px;

    ${plume.glyphs.stroke.CheckCircle} {
      color: ${plume.COLORS.PRIMARY_GREEN};
      margin-right: 6px;
      margin-top: 2px;
    }

    ${plume.glyphs.stroke.TimesCircle} {
      color: ${plume.COLORS.PRIMARY_RED};
      margin-right: 6px;
      margin-top: 2px;
    }

    ${plume.Tooltip} {
      margin-bottom: 16px;

      ${plume.styles.copy.S.regular} {
        margin-bottom: 0;
      }
    }
  }
`

const Screenshot = styled(BrowserScreenshot)`
  max-width: 270px;
  width: 100%;
  margin-top: -20px;

  @media screen and ${plume.BREAKPOINTS.MOBILE_M} {
    max-width: 327px;
  }

  @media screen and ${plume.BREAKPOINTS.MOBILE_L} {
    max-width: 377px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    max-width: 522px;
  }
`

const Texts = styled.div`
  text-align: center;

  ${plume.styles.heading.L} {
    margin: 25px 0 20px;
    text-align: center;
  }

  ${plume.styles.copy.S.regular} {
    max-width: 456px;
    text-align: center;
    margin: 0 auto 45px;
  }

  ${plume.illustrations.Illustration} {
    margin-top: 42px;
  }

  ${CopyInput} {
    max-width: 586px;
    margin: 20px 0;
    width: 100%;
  }
`

const ShareOverlayModal = styled(plume.OverlayModal)`
  top: ${plume.SIZES.HEADER_DESKTOP_HEIGHT};

  ${plume.styles.modal.Body} {
    display: flex;
    flex-direction: column;
    align-items: center;

    ${Screenshot} {
      margin-bottom: 20px;
    }
  }
`
